import type { NotificationPublic, UnreadNotificationsCount } from '@repo/types/notifications';

import { NotificationKey } from '@repo/api/notification/keys';
import { useGetNotificationsInfinite as useGetNotificationsInfiniteBase } from '@repo/api/notification/useGetNotificationsInfinite';
import { NotificationStatus } from '@repo/types/enums/notificationStatus';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Aggregates notification counts by type from an array of notifications.
 *
 * @param notifications - Array of notifications to count
 * @returns Record mapping notification type to count
 */
function aggregateCountsByType(notifications: NotificationPublic[]): Record<string, number> {
  return notifications.reduce(
    (accumulator, notification) => {
      const { type } = notification;
      accumulator[type] = (accumulator[type] || 0) + 1;
      return accumulator;
    },
    {} as Record<string, number>
  );
}

/**
 * Updates the unread notifications count cache with new notification counts.
 *
 * @param currentCounts - Current cached unread notification counts
 * @param newCountsByType - New counts to add, grouped by notification type
 * @returns Updated unread notification counts array
 */
function updateUnreadCounts(currentCounts: UnreadNotificationsCount[] | undefined, newCountsByType: Record<string, number>): UnreadNotificationsCount[] | undefined {
  if (!currentCounts) {
    return undefined;
  }

  return currentCounts.map((item) => ({
    ...item,
    count: String(Math.max(0, Number(item.count) + (newCountsByType[item.type] || 0))),
  }));
}

/**
 * Handles incoming SSE message events containing new notifications.
 * Updates the query cache with invalidation and count updates.
 *
 * @param queryClient - React Query client instance
 * @param event - SSE MessageEvent containing notification data
 */
function handleSSEMessage(queryClient: QueryClient, event: MessageEvent): void {
  try {
    const newNotifications: NotificationPublic[] = JSON.parse(event.data);

    // Skip processing if no new notifications
    if (newNotifications.length === 0) {
      return;
    }

    // Invalidate notifications list to trigger refetch
    queryClient.invalidateQueries({
      queryKey: [NotificationKey.NOTIFICATIONS_INFINITE],
    });

    // Optimistically update unread counts
    const countsByType = aggregateCountsByType(newNotifications);

    queryClient.setQueryData<UnreadNotificationsCount[] | undefined>([NotificationKey.UNREAD_NOTIFICATIONS_COUNT], (currentCounts) => updateUnreadCounts(currentCounts, countsByType));
  } catch (error) {
    console.error('Failed to parse SSE notification event data:', error);
  }
}

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

/**
 * Exponential backoff with jitter, so a backend restart doesn't get hammered by
 * every open tab reconnecting on the same tick.
 *
 * @param attempt - Number of consecutive failed connection attempts
 * @returns Delay in milliseconds before the next attempt
 */
function reconnectDelay(attempt: number): number {
  const backoff = Math.min(INITIAL_RECONNECT_DELAY_MS * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
  return backoff / 2 + Math.random() * (backoff / 2);
}

/**
 * Creates and configures an SSE EventSource connection for real-time notifications.
 *
 * @param queryClient - React Query client instance for cache updates
 * @param onFatalError - Invoked when the connection fails terminally
 * @param onOpen - Invoked once the connection is established
 * @returns Configured EventSource instance
 */
function createNotificationEventSource(queryClient: QueryClient, onFatalError: () => void, onOpen: () => void): EventSource {
  const eventSource = new EventSource(`${import.meta.env.VITE_API_DOMAIN}/api/notification/sse`, {
    withCredentials: true,
  });

  eventSource.onopen = onOpen;

  eventSource.onmessage = (event: MessageEvent) => {
    handleSSEMessage(queryClient, event);
  };

  eventSource.onerror = (error) => {
    console.error('SSE connection error:', error);

    // A non-200 status or a wrong content-type is a terminal failure: the
    // browser will not retry on its own, so notifications would stay dead
    // until a page reload. While the readyState is CONNECTING the browser is
    // already retrying — reconnecting here too would open a second stream.
    if (eventSource.readyState === EventSource.CLOSED) {
      onFatalError();
    }
  };

  return eventSource;
}

/**
 * Custom hook for fetching notifications with real-time updates via SSE.
 *
 * This hook extends the base `useGetNotificationsInfinite` hook by adding
 * Server-Sent Events (SSE) subscription for real-time notification updates.
 *
 * Features:
 * - Infinite scroll pagination for notifications
 * - Real-time updates via SSE connection
 * - Automatic cache invalidation on new notifications
 * - Optimistic unread count updates
 *
 * @returns Infinite query result with notifications data and pagination controls
 *
 * @example
 * // Usage in a component:
 * const query = useGetNotificationsInfinite();
 * const notifications = query.data?.pages.flat() ?? [];
 * // Use query.fetchNextPage() to load more
 * // Check query.hasNextPage to show/hide load more button
 */
export function useGetNotificationsInfinite(filter: NotificationStatus = NotificationStatus.All) {
  const queryClient = useQueryClient();

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);

  /**
   * Memoized SSE setup function to prevent unnecessary effect reruns.
   */
  const setupSSEConnection = useCallback(() => {
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;

      eventSourceRef.current = createNotificationEventSource(
        queryClient,
        () => {
          eventSourceRef.current?.close();
          eventSourceRef.current = null;
          if (cancelled) return;

          reconnectTimerRef.current = setTimeout(connect, reconnectDelay(attemptRef.current));
          attemptRef.current += 1;
        },
        () => {
          attemptRef.current = 0;
        }
      );
    };

    connect();

    // Cleanup: close the stream and drop any reconnect still pending.
    return () => {
      cancelled = true;
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [queryClient]);

  // Establish SSE connection on mount, cleanup on unmount
  useEffect(() => {
    const cleanup = setupSSEConnection();
    return cleanup;
  }, [setupSSEConnection]);

  return useGetNotificationsInfiniteBase(filter);
}
